import React, { useRef, useState } from "react";
import { Upload, Link2, Save, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function EditProfileCard({ profile, onUpdated }) {
  const { setUser } = useAuth();
  const [name, setName] = useState(profile?.name || "");
  const [pictureUrl, setPictureUrl] = useState(
    profile?.picture && !profile.picture.startsWith("data:") ? profile.picture : ""
  );
  const [bio, setBio] = useState(profile?.bio || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/me", {
        name: name.trim() || profile.name,
        picture: pictureUrl.trim() || null,
        bio: bio.trim(),
      });
      setUser?.(data);
      toast.success("Profile updated");
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be ≤ 2MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/me/avatar", fd);
      setUser?.(data);
      toast.success("Avatar uploaded");
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <section
      data-testid="edit-profile-card"
      className="mb-8 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5"
    >
      <h2 className="font-display text-lg font-bold mb-3">Edit your profile</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Display name</div>
          <input value={name} onChange={(e) => setName(e.target.value)}
            data-testid="profile-name-input"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]" />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1 flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Picture URL (or upload a file below)
          </div>
          <input value={pictureUrl} onChange={(e) => setPictureUrl(e.target.value)}
            data-testid="profile-picture-url"
            placeholder="https://...png"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] font-mono-tt" />
        </label>
        <label className="block md:col-span-2">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1 flex items-center justify-between">
            <span>Bio (one line, shows on your profile)</span>
            <span className="text-[10px] font-mono-tt">{bio.length}/240</span>
          </div>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 240))}
            data-testid="profile-bio-input"
            placeholder="Cinephile, completionist, always chasing the next 4K release."
            rows={2}
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] resize-none"
          />
        </label>
        <div className="md:col-span-2 flex items-center gap-3 flex-wrap">
          <input ref={fileRef} type="file" accept="image/*" onChange={upload}
            data-testid="profile-picture-upload"
            className="hidden" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="profile-picture-upload-btn"
            className="bg-[var(--tt-surface-2)] hover:bg-[#222] border border-[var(--tt-border)] text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload image (≤ 2MB)
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="profile-save-btn"
            className="ml-auto bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-semibold text-sm px-4 py-2 rounded-full flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </section>
  );
}
